import { useNavigate } from 'react-router-dom';

import {
  CompetencyCard,
  ContentCompetency,
  TrackAssessmentCategory,
  UnlockCard,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';

import { Box, SxProps, Theme, Typography } from '@mui/material';

export type ProsperContentView = 'BY_UNLOCK' | 'ALL';

type CompetenciesByUnlock = Record<
  number,
  { unlock: TrackAssessmentCategory; competencies: ContentCompetency[] }
>;

type ProsperContentProps = Readonly<{
  contentCompetencies: ContentCompetency[];
  pageView?: ProsperContentView;
}>;

export const ProsperContent = ({
  contentCompetencies,
  pageView = 'BY_UNLOCK',
}: ProsperContentProps) => {
  const navigate = useNavigate();

  // ALL view - show all competencies in one big grid
  if (pageView === 'ALL') {
    return (
      <Box sx={gridStyle}>
        {contentCompetencies.map((competency) => {
          return (
            <Box key={competency.id} sx={competencyCardWrapperStyle}>
              <CompetencyCard
                competency={competency}
                onClick={() => navigate(`${APP_ROUTE.COMPETENCY}/${competency.slug}`)}
              />
            </Box>
          );
        })}
      </Box>
    );
  }

  // BY_UNLOCK view - group competencies by unlock
  const competenciesByUnlock: CompetenciesByUnlock = contentCompetencies.reduce(
    (acc, competency) => {
      const unlock = competency.unlock;

      if (!acc[unlock.id]) {
        acc[unlock.id] = {
          unlock,
          competencies: [],
        };
      }

      acc[unlock.id].competencies.push(competency);

      return acc;
    },
    {} as CompetenciesByUnlock,
  );

  // sort by unlock id
  const sortedUnlocks = Object.values(competenciesByUnlock).sort(
    (a, b) => a.unlock.id - b.unlock.id,
  );

  return (
    <Box sx={scrollRowsContainerStyle}>
      {sortedUnlocks.map(({ unlock, competencies }) => {
        return (
          <Box key={unlock.id} sx={rowGroupStyle}>
            <Typography
              variant="text-xl-semibold"
              color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
            >
              {unlock.name}
            </Typography>
            <Box sx={horizontalScrollRowStyle}>
              <Box sx={scrollItemStyle}>
                <UnlockCard
                  variant={{
                    type: 'TINY',
                    category: unlock,
                  }}
                />
              </Box>
              {competencies.map((competency) => {
                return (
                  <Box key={competency.id} sx={scrollItemStyle}>
                    <CompetencyCard
                      competency={competency}
                      onClick={() => navigate(`${APP_ROUTE.COMPETENCY}/${competency.slug}`)}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const gridStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gap: backrs.spacing['spacing-2xl'],
  pb: backrs.spacing['spacing-2xl'],
  gridTemplateColumns: `repeat(auto-fill, minmax(min(310px, 100%), 1fr))`,
  padding: backrs.spacing['spacing-xs'],
});

const scrollRowsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  pb: backrs.spacing['spacing-2xl'],
});

const rowGroupStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const horizontalScrollRowStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-md'],
});

const scrollItemStyle: SxProps<Theme> = {
  minWidth: '310px',
  maxWidth: '310px',
  flexShrink: 0,
};

const competencyCardWrapperStyle: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  width: '100%',
};
